import { IRetryPolicy, RetryContext } from '@microsoft/signalr';

/**
 * Reconnect delays for the academy live hub: exponential backoff, base 500ms, capped at 60s
 * (contracts/realtime-hub.md) — counted across connections, not only within one reconnect.
 *
 * SignalR restarts `previousRetryCount` at 0 every time a connection drops, and asks for the
 * first delay before anything else (`@microsoft/signalr/dist/cjs/HubConnection.js:690`). A
 * policy that reads only that number answers "retry at once" to every drop. That is right for a
 * connection that had been up for a while, and wrong for one the server closes as soon as it
 * opens: the reconnect succeeds, the count goes back to 0, and the client loops with no pause at
 * all. A hub whose Redis backplane is unreachable does exactly that — the handshake completes
 * and the socket is closed with 1011 — and one client was measured reconnecting 443 times in
 * 10 seconds against it.
 *
 * So a drop is judged by how long the connection it ends had lived. One that held for
 * `stableAfterMs` is retried at once, as before. One that died sooner adds to a streak, and the
 * streak is what the backoff is computed from; a retry that fails inside the streak keeps
 * climbing from there. The first connection that holds clears it.
 *
 * The pause a streak alone can ask for stops at `streakMaxDelayMs`, well short of the 60s. The
 * streak is there to end the loop with no pause, and a few seconds do that; a minute is what
 * someone on a network that drops every twenty seconds would then wait, with no chat and no
 * status change, for a connection that would have come back at once. The 60s remain for the
 * retry that fails outright — the server is not answering, and nothing is lost by waiting.
 *
 * The service tells the policy when a connection comes up (`connected`) and when it is torn
 * down on purpose (`reset`); the clock is passed in so the spec can move it.
 */
export class AcademyReconnectPolicy implements IRetryPolicy {
  private readonly maxDelayMs = 60000;
  private readonly baseDelayMs = 500;
  /**
   * Above the 6–17s a connection survives when the backplane is down (the Redis client's own
   * timeouts), and far below any connection worth calling healthy.
   */
  private readonly stableAfterMs = 30000;
  /** The most a streak of young deaths, on its own, makes the first retry wait. */
  private readonly streakMaxDelayMs = 8000;

  private connectedAtMs: number | null = null;
  private shortLivedDrops = 0;

  constructor(private readonly nowMs: () => number) {}

  /** The connection is up: the first start, or a reconnect that went through. */
  public connected(): void {
    this.connectedAtMs = this.nowMs();
  }

  /** The connection was stopped on purpose: whatever comes next starts clean. */
  public reset(): void {
    this.connectedAtMs = null;
    this.shortLivedDrops = 0;
  }

  public nextRetryDelayInMilliseconds(retryContext: RetryContext): number {
    if (retryContext.previousRetryCount === 0) this.noteDrop();
    const failures = this.shortLivedDrops + retryContext.previousRetryCount;
    if (failures === 0) return 0;
    const cap = retryContext.previousRetryCount === 0 ? this.streakMaxDelayMs : this.maxDelayMs;
    return Math.min(this.baseDelayMs * Math.pow(2, failures - 1), cap);
  }

  /**
   * `previousRetryCount === 0` is SignalR reporting that a live connection has just dropped. A
   * drop nobody announced the connection of (`connected` never called) is taken as a stable one.
   */
  private noteDrop(): void {
    const diedYoung =
      this.connectedAtMs !== null && this.nowMs() - this.connectedAtMs < this.stableAfterMs;
    this.shortLivedDrops = diedYoung ? this.shortLivedDrops + 1 : 0;
  }
}
